<template>
  <div class="modal fade" id="paymentModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header text-white bg-primary">
          <h5 class="modal-title" style="color: white">
            <i class="bx bx-money me-2"></i>Record Premium Payment
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Payment Content -->
          <div v-if="!processing && !transactionSuccess && !showAgencyBankingSlip" class="transaction-content">
            <div class="row">
              <!-- Invoice Details -->
              <div class="col-sm-12 col-md-7 mb-3">
                <div class="table-responsive bg-light p-4 h-100">
                  <table class="table table-light">
                    <thead>
                    <tr style="border-bottom: 5px solid black;">
                      <th colspan="4" class="text-capitalize">
                        <h4 class="mb-0 fw-bold text-black">Payment Details</h4>
                        <p class="mb-0">Member: {{ memberFullName }}</p>
                        <p class="mb-0 text-info" v-if="selectedPlan">
                          Plan: {{ selectedPlan.plan_name }}
                        </p>
                      </th>
                    </tr>
                    </thead>
                    <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>QTY</th>
                      <th class="text-right">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <!-- Primary Member -->
                    <tr>
                      <td><strong>1.</strong></td>
                      <td>Primary CBHI membership fee</td>
                      <td>1</td>
                      <td class="text-right text-dark fw-semibold">
                        <strong>ETB {{ formatCurrency(primaryRate) }}</strong>
                      </td>
                    </tr>

                    <!-- Free Dependents -->
                    <tr v-if="freeDependents > 0">
                      <td><strong>2.</strong></td>
                      <td>
                        Free Dependents (Immediate Family)
                        <div class="small text-muted mt-1">
                          <div v-for="(dep, index) in getFreeImmediateFamilyList()" :key="'free-' + index" class="ms-2">
                            • {{ dep }}
                          </div>
                        </div>
                      </td>
                      <td>{{ freeDependents }}</td>
                      <td class="text-right text-success fw-semibold">
                        <strong>FREE</strong>
                      </td>
                    </tr>

                    <!-- Paid Dependents -->
                    <tr v-if="paidDependents > 0">
                      <td><strong>3.</strong></td>
                      <td>
                        Additional Dependents @ ETB {{ formatCurrency(dependentRate) }} each
                        <div class="small text-muted mt-1">
                          <div v-for="(dep, index) in getPaidDependentsList()" :key="'paid-' + index" class="ms-2">
                            • {{ dep }}
                          </div>
                        </div>
                      </td>
                      <td>{{ paidDependents }}</td>
                      <td class="text-right text-dark fw-semibold">
                        <strong>ETB {{ formatCurrency(paidDependents * dependentRate) }}</strong>
                      </td>
                    </tr>

                    <!-- Subtotal row -->
                    <tr style="border-top: 2px solid #dee2e6;">
                      <td colspan="3" class="text-right">
                        <strong>Annual Premium Total:</strong>
                      </td>
                      <td class="text-right text-dark fw-semibold">
                        <strong>ETB {{ formatCurrency(fullAnnualPremium) }}</strong>
                      </td>
                    </tr>

                    <!-- Previous payments row -->
                    <tr v-if="totalPaidPremiums > 0" class="text-success">
                      <td colspan="3" class="text-right">
                        <strong>Already Paid:</strong>
                      </td>
                      <td class="text-right fw-semibold">
                        <strong>- ETB {{ formatCurrency(totalPaidPremiums) }}</strong>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                      <td colspan="4" class="text-muted">
                        <small>
                          <i class="bx bx-info-circle"></i>
                          Total beneficiaries: {{ totalBeneficiaries }}
                          (1 primary + {{ totalDependents }} dependents:
                          {{ freeDependents }} free, {{ paidDependents }} paid)
                        </small>
                      </td>
                    </tr>
                    <tr style="border-top: 3px solid black; border-bottom: 3px solid black;">
                      <th class="text-right text-dark fw-bold" colspan="4">
                        <small class="text-uppercase">Balance Due</small>
                        <h4 class="mb-0 text-danger">ETB {{ formatCurrency(balanceDue) }}</h4>
                      </th>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <!-- Payment Methods -->
              <div class="col-sm-12 col-md-5 mb-3">
                <div class="p-3 rounded-1 border border-dashed border-gray-5 py-4">
                  <h4 class="mb-0 pb-0 fw-semibold d-block">Select Payment Method</h4>
                  <p class="pb-0 mb-0">
                    <span class="text-muted">Balance to Pay: <strong class="text-danger">ETB {{ formatCurrency(balanceDue) }}</strong></span>
                  </p>
                </div>

                <!-- ✨ No Payment Methods Warning -->
                <div v-if="!hasAnyPaymentMethod" class="alert alert-warning mt-3">
                  <div class="d-flex align-items-center">
                    <i class="bx bx-error-circle fs-3 me-3"></i>
                    <div>
                      <h6 class="alert-heading mb-1">No Payment Methods Available</h6>
                      <p class="mb-0 small">
                        You don't have permission to accept any payment methods. Please contact your administrator to request access.
                      </p>
                    </div>
                  </div>
                </div>

                <div v-else class="accordion mt-3">
                  <!-- Mobile Money -->
                  <div class="accordion-item" v-if="getRight('ACCEPT MOBILE MONEY PAYMENT')">
                    <h2 class="accordion-header">
                      <button class="accordion-button fw-medium" type="button" @click="selectedPaymentMethod = 'mobile'">
                        <div class="flex-shrink-0 me-3">
                          <i class="bx bx-mobile-alt fs-1 text-primary"></i>
                        </div>
                        <div class="d-flex flex-column">
                          <p class="m-0 p-0 text-uppercase fw-semibold">Mobile Money Wallets</p>
                          <p class="mb-0 p-0"><small>Use this option for clients using Telebirr or MPESA</small></p>
                        </div>
                      </button>
                    </h2>
                    <div v-if="selectedPaymentMethod === 'mobile'" class="accordion-collapse collapse show">
                      <div class="accordion-body">
                        <div class="form-group mb-3">
                          <label for="wallet">Select Mobile Wallet</label>
                          <select v-model="paymentForm.paymentMethodId" @change="savePaymentPreferences" class="form-select">
                            <option value="">Choose Wallet</option>
                            <option v-for="method in mobilePaymentMethods" :key="method.method_id" :value="method.method_id">
                              {{ method.method_name }}
                            </option>
                          </select>
                          <small class="text-muted">Your selection will be remembered for next time</small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Ethiopian Mobile Number <span class="text-danger">*</span></label>
                          <div class="position-relative">
                            <div class="input-group">
                              <span class="input-group-text">🇪🇹 +251</span>
                              <input
                                  v-model="paymentForm.mobileNumber"
                                  @blur="savePaymentPreferences"
                                  @input="validateMobileNumber"
                                  type="tel"
                                  class="form-control"
                                  :class="getMobileNumberClass()"
                                  placeholder="911234567"
                                  maxlength="9"
                                  pattern="[79]\d{8}">
                            </div>
                            <div v-if="mobileValidation.checking" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="visually-hidden">Checking...</span>
                              </div>
                            </div>
                            <div v-else-if="mobileValidation.status === 'valid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-check-circle text-success fs-5"></i>
                            </div>
                            <div v-else-if="mobileValidation.status === 'invalid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-x-circle text-danger fs-5"></i>
                            </div>
                          </div>
                          <small v-if="mobileValidation.message" :class="mobileValidation.messageClass">
                            <i :class="mobileValidation.icon"></i> {{ mobileValidation.message }}
                          </small>
                          <small v-else class="text-muted">
                            <i class="bx bx-info-circle"></i> Enter 9 digits starting with 7 or 9 (e.g., 911234567)
                          </small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Amount (ETB)</label>
                          <input readonly v-model="paymentForm.mobileAmount" type="number" class="form-control" placeholder="Enter Amount" min="0" step="0.01">
                          <small class="text-muted">
                            Balance Due: ETB {{ formatCurrency(balanceDue) }}
                            <span v-if="totalPaidPremiums > 0" class="text-success">
                              (ETB {{ formatCurrency(totalPaidPremiums) }} already paid)
                            </span>
                          </small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Payment Reference/Transaction ID</label>
                          <div class="position-relative">
                            <input
                                v-model="paymentForm.mobileReference"
                                type="text"
                                class="form-control"
                                :class="getPaymentReferenceClass('mobile')"
                                placeholder="Enter transaction reference from mobile wallet"
                                @input="validatePaymentReference('mobile')"
                                @blur="checkPaymentReferenceAvailability('mobile')">
                            <div v-if="paymentReferenceValidation.fieldType === 'mobile' && paymentReferenceValidation.checking" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="visually-hidden">Checking...</span>
                              </div>
                            </div>
                            <div v-else-if="paymentReferenceValidation.fieldType === 'mobile' && paymentReferenceValidation.status === 'valid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-check-circle text-success fs-5"></i>
                            </div>
                            <div v-else-if="paymentReferenceValidation.fieldType === 'mobile' && paymentReferenceValidation.status === 'invalid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-x-circle text-danger fs-5"></i>
                            </div>
                          </div>
                          <small v-if="paymentReferenceValidation.fieldType === 'mobile' && paymentReferenceValidation.message" :class="paymentReferenceValidation.messageClass">
                            <i :class="paymentReferenceValidation.icon"></i> {{ paymentReferenceValidation.message }}
                          </small>
                          <small v-else class="text-muted">
                            <i class="bx bx-info-circle"></i> Enter the transaction ID from your mobile wallet
                          </small>
                        </div>
                        <div class="mt-3">
                          <button @click="processPayment" class="btn btn-primary w-100 text-uppercase" :disabled="!canProcessMobilePayment">
                            <span v-if="paymentLoading" class="spinner-border spinner-border-sm me-2"></span>
                            {{ paymentLoading ? 'Processing...' : `Record Payment of ETB ${formatCurrency(balanceDue)}` }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Cash Payment -->
                  <div class="accordion-item" v-if="getRight('ACCEPT CASH PAYMENT')">
                    <h2 class="accordion-header">
                      <button class="accordion-button fw-medium collapsed" type="button" @click="selectedPaymentMethod = 'cash'">
                        <div class="flex-shrink-0 me-3">
                          <i class="bx bx-money fs-1 text-success"></i>
                        </div>
                        <div class="d-flex flex-column">
                          <p class="m-0 p-0 text-uppercase fw-semibold">CASH</p>
                          <p class="mb-0 p-0"><small>Use this option when you have received direct cash payment</small></p>
                        </div>
                      </button>
                    </h2>
                    <div v-if="selectedPaymentMethod === 'cash'" class="accordion-collapse collapse show">
                      <div class="accordion-body">
                        <div class="mb-3">
                          <label>Amount Received (ETB)</label>
                          <input readonly v-model="paymentForm.cashAmount" type="number" class="form-control" placeholder="Enter Amount" min="0" step="0.01">
                          <small class="text-muted">
                            Balance Due: ETB {{ formatCurrency(balanceDue) }}
                            <span v-if="totalPaidPremiums > 0" class="text-success">
                              (ETB {{ formatCurrency(totalPaidPremiums) }} already paid)
                            </span>
                          </small>
                        </div>
                        <div class="form-check form-check-primary mb-3">
                          <input v-model="paymentForm.cashConfirmed" class="form-check-input" type="checkbox" id="cashConfirmCheck">
                          <label class="form-check-label" for="cashConfirmCheck">
                            I confirm that I have received cash payment of <strong>ETB {{ formatCurrency(balanceDue) }}</strong> from the client
                          </label>
                        </div>
                        <button @click="processPayment" class="btn btn-primary w-100 text-uppercase" :disabled="!canProcessCashPayment">
                          <span v-if="paymentLoading" class="spinner-border spinner-border-sm me-2"></span>
                          {{ paymentLoading ? 'Processing...' : `Complete Payment of ETB ${formatCurrency(balanceDue)}` }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Bank Payment -->
                  <div class="accordion-item" v-if="getRight('ACCEPT BANK TRANSFER PAYMENT')">
                    <h2 class="accordion-header">
                      <button class="accordion-button fw-medium collapsed" type="button" @click="selectedPaymentMethod = 'bank'">
                        <div class="flex-shrink-0 me-3">
                          <i class="bx bx-building-house fs-1 text-info"></i>
                        </div>
                        <div class="d-flex flex-column">
                          <p class="m-0 p-0 text-uppercase fw-semibold">BANK TRANSFER</p>
                          <p class="mb-0 p-0"><small>Use this option when client paid via bank and has deposit slip</small></p>
                        </div>
                      </button>
                    </h2>
                    <div v-if="selectedPaymentMethod === 'bank'" class="accordion-collapse collapse show">
                      <div class="accordion-body">
                        <div class="form-group mb-3">
                          <label>Select Bank</label>
                          <select v-model="paymentForm.bankMethodId" @change="savePaymentPreferences" class="form-select" required>
                            <option value="">Choose Bank</option>
                            <option v-for="bank in bankPaymentMethods" :key="bank.method_id" :value="bank.method_id">
                              {{ bank.method_name }}
                            </option>
                          </select>
                          <small class="text-muted">Your bank selection will be remembered for next time</small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Amount Paid (ETB)</label>
                          <input readonly v-model="paymentForm.bankAmount" type="number" class="form-control" placeholder="Enter Amount" min="0" step="0.01">
                          <small class="text-muted">
                            Balance Due: ETB {{ formatCurrency(balanceDue) }}
                            <span v-if="totalPaidPremiums > 0" class="text-success">
                              (ETB {{ formatCurrency(totalPaidPremiums) }} already paid)
                            </span>
                          </small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Bank Reference/Deposit Slip Number</label>
                          <div class="position-relative">
                            <input
                                v-model="paymentForm.bankReference"
                                type="text"
                                class="form-control"
                                :class="getPaymentReferenceClass('bank')"
                                placeholder="Enter bank reference or deposit slip number"
                                @input="validatePaymentReference('bank')"
                                @blur="checkPaymentReferenceAvailability('bank')">
                            <div v-if="paymentReferenceValidation.fieldType === 'bank' && paymentReferenceValidation.checking" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="visually-hidden">Checking...</span>
                              </div>
                            </div>
                            <div v-else-if="paymentReferenceValidation.fieldType === 'bank' && paymentReferenceValidation.status === 'valid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-check-circle text-success fs-5"></i>
                            </div>
                            <div v-else-if="paymentReferenceValidation.fieldType === 'bank' && paymentReferenceValidation.status === 'invalid'" class="position-absolute top-50 end-0 translate-middle-y me-3">
                              <i class="bx bx-x-circle text-danger fs-5"></i>
                            </div>
                          </div>
                          <small v-if="paymentReferenceValidation.fieldType === 'bank' && paymentReferenceValidation.message" :class="paymentReferenceValidation.messageClass">
                            <i :class="paymentReferenceValidation.icon"></i> {{ paymentReferenceValidation.message }}
                          </small>
                          <small v-else class="text-muted">
                            <i class="bx bx-info-circle"></i> Enter the reference number from the bank deposit slip
                          </small>
                        </div>
                        <div class="form-group mb-3">
                          <label>Notes (Optional)</label>
                          <textarea v-model="paymentForm.notes" class="form-control" rows="2" placeholder="Add any additional notes about the bank payment"></textarea>
                        </div>
                        <div class="mt-3">
                          <button @click="processPayment" class="btn btn-primary w-100 text-uppercase" :disabled="!canProcessBankPayment">
                            <span v-if="paymentLoading" class="spinner-border spinner-border-sm me-2"></span>
                            {{ paymentLoading ? 'Processing...' : `Record Payment of ETB ${formatCurrency(balanceDue)}` }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Agency Banking Payment -->
                  <div class="accordion-item" v-if="getRight('GENERATE AGENCY BANKING SLIP')">
                    <h2 class="accordion-header">
                      <button class="accordion-button fw-medium collapsed" type="button" @click="selectedPaymentMethod = 'agency_banking'">
                        <div class="flex-shrink-0 me-3">
                          <i class="bx bx-store fs-1 text-warning"></i>
                        </div>
                        <div class="d-flex flex-column">
                          <p class="m-0 p-0 text-uppercase fw-semibold">Pay Via Agency Banking</p>
                          <p class="mb-0 p-0"><small>Generate payment slip for client to pay at bank branch or agent</small></p>
                        </div>
                      </button>
                    </h2>
                    <div v-if="selectedPaymentMethod === 'agency_banking'" class="accordion-collapse collapse show">
                      <div class="accordion-body">
                        <div class="alert alert-info mb-3">
                          <i class="bx bx-info-circle me-2"></i>
                          <strong>Agency Banking Payment</strong>
                          <p class="mb-0 small mt-2">
                            This option will generate a professional payment slip that the client can take to any bank branch or agency banking location to make their payment.
                          </p>
                        </div>

                        <div class="card border-warning mb-3">
                          <div class="card-body">
                            <h6 class="card-title text-warning"><i class="bx bx-money-withdraw me-2"></i>Payment Amount</h6>
                            <h3 class="text-dark mb-0">ETB {{ formatCurrency(balanceDue) }}</h3>
                            <small class="text-muted">Annual CBHI Premium</small>
                          </div>
                        </div>

                        <div class="card border-primary mb-3">
                          <div class="card-body">
                            <h6 class="card-title text-primary"><i class="bx bx-id-card me-2"></i>CBHI Number</h6>
                            <h4 class="text-dark mb-0 font-monospace">{{ memberInfo.cbhi_number || memberInfo.cbhiNumber || 'N/A' }}</h4>
                            <small class="text-muted">Member will present this at the bank</small>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">
                            <i class="bx bx-note me-2"></i>Additional Notes (Optional)
                          </label>
                          <textarea
                              v-model="paymentForm.agencyBankingNotes"
                              class="form-control"
                              rows="2"
                              placeholder="Any special instructions or notes for the member..."></textarea>
                          <small class="text-muted">These notes will appear on the payment slip</small>
                        </div>

                        <button @click="generateAgencyBankingSlip" class="btn btn-warning w-100 text-uppercase">
                          <i class="bx bx-printer me-2"></i>
                          Generate Payment Slip
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Agency Banking Payment Slip (Printable) -->
          <div v-if="showAgencyBankingSlip" class="agency-banking-slip">
            <!-- Print Preview Container -->
            <div id="paymentSlipPrintArea" class="payment-slip-container p-4">
              <!-- Header Section -->
              <div class="slip-header text-center border-bottom pb-2 mb-2">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="text-end">
                    <h5 class="mb-0 text-primary">🇪🇹 Ethiopian CBHI</h5>
                    <small class="text-muted">Community-Based Health Insurance</small>
                  </div>
                </div>
                <h3 class="text-uppercase fw-bold text-dark mb-1">Agency Banking Payment Slip</h3>
                <p class="text-muted mb-0">Please present this slip at any authorized bank branch or agency location</p>
              </div>

              <!-- Important Notice Banner -->
              <div class="alert alert-warning border-warning mb-2">
                <div class="d-flex align-items-center">
                  <i class="bx bx-info-circle fs-1 me-3"></i>
                  <div>
                    <h6 class="alert-heading mb-1">Important Instructions</h6>
                    <p class="mb-0 small">
                      Present this payment slip along with your <strong>CBHI Number</strong> at any authorized bank branch or agency banking location.
                      Keep this slip until payment is confirmed.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Member Information Section -->
              <div class="row mb-2">
                <div class="col-12">
                  <div class="card border">
                    <div class="card-header bg-primary text-white">
                      <h6 class="mb-0 fw-bold" style="color: white;">
                        <i class="bx bx-user me-2"></i>MEMBER INFORMATION
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6 mb-2">
                          <label class="text-muted small fw-bold text-uppercase">CBHI Number</label>
                          <div class="h4 mb-0 font-monospace text-primary">
                            {{ memberInfo.cbhi_number || memberInfo.cbhiNumber || 'N/A' }}
                          </div>
                          <small class="text-danger">⚠️ Present this number at the bank</small>
                        </div>
                        <div class="col-md-6 mb-2">
                          <label class="text-muted small fw-bold text-uppercase">Member Name</label>
                          <div class="h5 mb-0">{{ memberFullName }}</div>
                        </div>
                        <div class="col-md-6 mb-2">
                          <label class="text-muted small fw-bold text-uppercase">Phone Number</label>
                          <div>{{ memberInfo.phone_number || 'N/A' }}</div>
                        </div>
                        <div class="col-md-6 mb-1">
                          <label class="text-muted small fw-bold text-uppercase">Address</label>
                          <div>{{ getFullAddress() }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Details Section -->
              <div class="row mb-2">
                <div class="col-12">
                  <div class="card border">
                    <div class="card-header bg-success text-white">
                      <h6 class="mb-0 fw-bold" style="color: white;">
                        <i class="bx bx-money me-2"></i>PAYMENT DETAILS
                      </h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-bordered mb-3">
                        <thead class="table-light">
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th class="text-center">Quantity</th>
                          <th class="text-end">Amount (ETB)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td>1</td>
                          <td>
                            <strong>Primary CBHI Membership</strong>
                            <div class="small text-muted">{{ selectedPlan ? selectedPlan.plan_name : 'N/A' }}</div>
                          </td>
                          <td class="text-center">1</td>
                          <td class="text-end fw-bold">{{ formatCurrency(primaryRate) }}</td>
                        </tr>
                        <tr v-if="freeDependents > 0">
                          <td>2</td>
                          <td>
                            <strong>Free Dependents (Immediate Family)</strong>
                            <div class="small text-muted">
                              <div v-for="(dep, index) in getFreeImmediateFamilyList()" :key="'free-' + index">
                                • {{ dep }}
                              </div>
                            </div>
                          </td>
                          <td class="text-center">{{ freeDependents }}</td>
                          <td class="text-end text-success fw-bold">FREE</td>
                        </tr>
                        <tr v-if="paidDependents > 0">
                          <td>3</td>
                          <td>
                            <strong>Additional Dependents</strong>
                            <div class="small text-muted">
                              @ ETB {{ formatCurrency(dependentRate) }} each
                              <div v-for="(dep, index) in getPaidDependentsList()" :key="'paid-' + index">
                                • {{ dep }}
                              </div>
                            </div>
                          </td>
                          <td class="text-center">{{ paidDependents }}</td>
                          <td class="text-end fw-bold">{{ formatCurrency(paidDependents * dependentRate) }}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr class="table-light">
                          <td colspan="3" class="text-end fw-bold">TOTAL ANNUAL PREMIUM:</td>
                          <td class="text-end">
                            <div class="h4 mb-0 text-success fw-bold">ETB {{ formatCurrency(fullAnnualPremium) }}</div>
                          </td>
                        </tr>
                        <tr v-if="totalPaidPremiums > 0" class="table-info">
                          <td colspan="3" class="text-end">Amount Already Paid:</td>
                          <td class="text-end text-success fw-bold">- ETB {{ formatCurrency(totalPaidPremiums) }}</td>
                        </tr>
                        <tr class="table-warning">
                          <td colspan="3" class="text-end fw-bold h5 mb-0">AMOUNT TO PAY:</td>
                          <td class="text-end">
                            <div class="h3 mb-0 text-danger fw-bold">ETB {{ formatCurrency(balanceDue) }}</div>
                          </td>
                        </tr>
                        </tfoot>
                      </table>

                      <div class="alert alert-success mb-0">
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Total Beneficiaries Covered:</strong> {{ totalBeneficiaries }}
                            <div class="small">
                              (1 Primary Member + {{ totalDependents }} Dependents: {{ freeDependents }} Free, {{ paidDependents }} Paid)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Plan Information -->
              <div class="row mb-2" v-if="selectedPlan">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <h6 class="mb-0 fw-bold" style="color: white;">
                        <i class="bx bx-shield-check me-2"></i>INSURANCE PLAN DETAILS
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <label class="text-muted small fw-bold">Plan Name</label>
                          <div class="fw-bold">{{ selectedPlan.plan_name }}</div>
                        </div>
                        <div class="col-md-4 mb-2">
                          <label class="text-muted small fw-bold">Plan Code</label>
                          <div class="font-monospace">{{ selectedPlan.plan_code || 'N/A' }}</div>
                        </div>
                        <div class="col-md-4 mb-2">
                          <label class="text-muted small fw-bold">Plan Type</label>
                          <div class="text-capitalize">{{ selectedPlan.plan_type || 'Standard' }}</div>
                        </div>
                        <div class="col-12 mt-2">
                          <label class="text-muted small fw-bold">Coverage Period</label>
                          <div>{{ getCurrentYear() }} (Valid for 12 months from payment date)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Instructions -->
              <div class="row mb-2">
                <div class="col-12">
                  <div class="card border-warning">
                    <div class="card-header bg-warning">
                      <h6 class="mb-0 fw-bold text-dark">
                        <i class="bx bx-info-circle me-2"></i>PAYMENT INSTRUCTIONS
                      </h6>
                    </div>
                    <div class="card-body">
                      <ol class="mb-0 ps-3">
                        <li class="mb-2">Visit any authorized bank branch or agency banking location</li>
                        <li class="mb-2">Present this payment slip to the teller or agent</li>
                        <li class="mb-2">Provide your <strong class="text-primary">CBHI Number: {{ memberInfo.cbhi_number || memberInfo.cbhiNumber || 'N/A' }}</strong></li>
                        <li class="mb-2">Pay the amount of <strong class="text-danger">ETB {{ formatCurrency(balanceDue) }}</strong></li>
                        <li class="mb-2">Collect your payment receipt from the bank</li>
                        <li class="mb-2">Keep this slip and bank receipt as proof of payment</li>
                        <li class="mb-0">Your membership will be activated within 24-48 hours of payment confirmation</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Notes -->
              <div v-if="paymentForm.agencyBankingNotes" class="row mb-2">
                <div class="col-12">
                  <div class="card border-secondary">
                    <div class="card-header bg-secondary text-white">
                      <h6 class="mb-0 fw-bold" style="color: white;">
                        <i class="bx bx-note me-2"></i>ADDITIONAL NOTES
                      </h6>
                    </div>
                    <div class="card-body">
                      <p class="mb-0">{{ paymentForm.agencyBankingNotes }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer Section -->
              <div class="row">
                <div class="col-12">
                  <div class="card border-0 bg-light">
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="text-muted small fw-bold">Generated Date</label>
                          <div>{{ getCurrentDateTime() }}</div>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="text-muted small fw-bold">Generated By</label>
                          <div>{{ getUsername() }}</div>
                        </div>
                        <div class="col-12">
                          <label class="text-muted small fw-bold">Reference Number</label>
                          <div class="font-monospace">{{ generateReferenceNumber() }}</div>
                        </div>
                      </div>

                      <hr class="my-3">

                      <div class="text-center">
                        <p class="mb-2 small text-muted">
                          <i class="bx bx-phone me-2"></i>For inquiries: +251-XXX-XXX-XXX
                          <span class="mx-2">|</span>
                          <i class="bx bx-envelope me-2"></i>Email: cbhi@ethiopia.gov.et
                        </p>
                        <p class="mb-0 small text-muted">
                          <i class="bx bx-info-circle me-2"></i>
                          This is a computer-generated payment slip. No signature is required.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Barcode / QR Code Section -->
              <div class="row mt-3">
                <div class="col-12 text-center">
                  <div class="p-3 border border-dashed">
                    <svg width="200" height="60" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" width="3" height="60" fill="black"/>
                      <rect x="12" width="2" height="60" fill="black"/>
                      <rect x="18" width="5" height="60" fill="black"/>
                      <rect x="27" width="2" height="60" fill="black"/>
                      <rect x="33" width="4" height="60" fill="black"/>
                      <rect x="41" width="2" height="60" fill="black"/>
                      <rect x="47" width="6" height="60" fill="black"/>
                      <rect x="57" width="3" height="60" fill="black"/>
                      <rect x="64" width="2" height="60" fill="black"/>
                      <rect x="70" width="4" height="60" fill="black"/>
                      <rect x="78" width="2" height="60" fill="black"/>
                      <rect x="84" width="5" height="60" fill="black"/>
                      <rect x="93" width="3" height="60" fill="black"/>
                      <rect x="100" width="2" height="60" fill="black"/>
                      <rect x="106" width="4" height="60" fill="black"/>
                      <rect x="114" width="2" height="60" fill="black"/>
                      <rect x="120" width="6" height="60" fill="black"/>
                      <rect x="130" width="2" height="60" fill="black"/>
                      <rect x="136" width="5" height="60" fill="black"/>
                      <rect x="145" width="3" height="60" fill="black"/>
                      <rect x="152" width="2" height="60" fill="black"/>
                      <rect x="158" width="4" height="60" fill="black"/>
                      <rect x="166" width="2" height="60" fill="black"/>
                      <rect x="172" width="5" height="60" fill="black"/>
                      <rect x="181" width="3" height="60" fill="black"/>
                      <rect x="188" width="2" height="60" fill="black"/>
                      <rect x="194" width="3" height="60" fill="black"/>
                    </svg>
                    <div class="small text-muted mt-2 font-monospace">{{ memberInfo.cbhi_number || memberInfo.cbhiNumber || 'N/A' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Print Action Buttons -->
            <div class="no-print p-4 bg-light border-top">
              <div class="row">
                <div class="col-md-6 mb-2">
                  <button @click="printPaymentSlip" class="btn btn-primary w-100">
                    <i class="bx bx-printer me-2"></i>Print Payment Slip
                  </button>
                </div>
                <div class="col-md-6 mb-2">
                  <button @click="closeAgencyBankingSlip" class="btn btn-secondary w-100">
                    <i class="bx bx-x me-2"></i>Close
                  </button>
                </div>
              </div>
              <div class="alert alert-info mt-3 mb-0">
                <i class="bx bx-info-circle me-2"></i>
                <strong>Note:</strong> The member should take this printed slip to any authorized bank branch or agency banking location to make the payment.
              </div>
            </div>
          </div>

          <!-- Processing Transaction -->
          <div v-if="processing" class="modal-content">
            <div class="modal-body">
              <div class="text-center py-5">
                <div class="mb-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                <h4>Processing Transaction</h4>
                <p>Be patient as the transaction is being processed. This may take a few seconds</p>
              </div>
            </div>
          </div>

          <!-- Success -->
          <div v-if="transactionSuccess" class="modal-content">
            <div class="modal-body">
              <div class="text-center py-5">
                <div class="mb-4">
                  <div class="success-checkmark">
                    <div class="check-icon">
                      <span class="icon-line line-tip"></span>
                      <span class="icon-line line-long"></span>
                      <div class="icon-circle"></div>
                      <div class="icon-fix"></div>
                    </div>
                  </div>
                </div>
                <h4 class="fw-bold text-success">Payment Recorded Successfully</h4>
                <p class="text-muted mb-2">
                  The premium payment has been successfully recorded for this member.
                </p>
                <div class="alert alert-success">
                  <p class="mb-1"><strong>Receipt Number:</strong> {{ paymentResult.receiptNumber }}</p>
                  <p class="mb-0"><strong>Amount Paid:</strong> ETB {{ formatCurrency(balanceDue) }}</p>
                </div>
                <p class="text-muted mb-4">
                  You may now view the receipt or close this window.
                </p>
                <div class="d-flex gap-3 justify-content-center">
                  <button class="btn btn-success" @click="viewReceipt">
                    <i class="bx bx-receipt me-2"></i>View Receipt
                  </button>
                  <button class="btn btn-outline-secondary" @click="closeModal">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson, getRight } from "@/api";

export default {
  name: "PaymentModal",
  props: {
    memberInfo: {
      type: Object,
      required: true
    },
    annualPremium: {
      type: Number,
      required: true
    },
    totalBeneficiaries: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      modalInstance: null,
      balanceDue: 0,
      totalPaidPremiums: 0,
      selectedPlan: null,
      fullAnnualPremium: 0,
      beneficiariesData: [],
      showAgencyBankingSlip: false,

      paymentForm: {
        paymentMethodId: null,
        mobileNumber: '',
        mobileAmount: 0,
        cashAmount: 0,
        bankAmount: 0,
        cashConfirmed: false,
        mobileReference: '',
        bankReference: '',
        bankMethodId: null,
        notes: '',
        agencyBankingNotes: ''
      },

      paymentMethods: [],

      mobileValidation: {
        checking: false,
        status: '',
        message: '',
        messageClass: 'text-muted',
        icon: 'bx bx-info-circle',
        isValid: false
      },

      paymentReferenceValidation: {
        checking: false,
        status: '',
        message: '',
        messageClass: 'text-muted',
        icon: 'bx bx-info-circle',
        isValid: false,
        fieldType: ''
      },

      processing: false,
      paymentLoading: false,
      transactionSuccess: false,
      selectedPaymentMethod: 'mobile',
      paymentResult: {}
    };
  },

  computed: {
    memberFullName() {
      return this.memberInfo.full_name || this.memberInfo.fullName || 'N/A';
    },

    totalDependents() {
      if (this.beneficiariesData && this.beneficiariesData.length > 0) {
        return this.beneficiariesData.length;
      }
      return this.totalBeneficiaries - 1;
    },

    primaryRate() {
      if (!this.selectedPlan) return 0;
      return parseFloat(this.selectedPlan.primary_annual_amount) || 0;
    },

    dependentRate() {
      if (!this.selectedPlan) return 0;
      return parseFloat(this.selectedPlan.primary_annual_amount) * parseFloat(this.selectedPlan.additional_member_rate) || 0;
    },

    maxFreeDependents() {
      return this.freeDependents;
    },

    freeDependents() {
      if (!this.beneficiariesData || !this.selectedPlan) return 0;

      const childAgeThreshold = this.getChildAgeThreshold();
      const spouseLimit = this.getSpouseLimit();
      let freeCount = 0;
      let spouseCount = 0;

      this.beneficiariesData.forEach(beneficiary => {
        const age = this.calculateAge(beneficiary.dateOfBirth || beneficiary.date_of_birth);

        if (beneficiary.relationship === 'spouse') {
          if (spouseLimit === 'unlimited' || spouseCount < spouseLimit) {
            freeCount++;
            spouseCount++;
          }
        } else if (beneficiary.relationship === 'child' && age < childAgeThreshold) {
          freeCount++;
        }
      });

      return freeCount;
    },

    paidDependents() {
      if (!this.beneficiariesData || !this.selectedPlan) return 0;

      const childAgeThreshold = this.getChildAgeThreshold();
      const spouseLimit = this.getSpouseLimit();
      let paidCount = 0;
      let spouseCount = 0;

      this.beneficiariesData.forEach(beneficiary => {
        const age = this.calculateAge(beneficiary.dateOfBirth || beneficiary.date_of_birth);

        if (beneficiary.relationship === 'spouse') {
          if (spouseLimit !== 'unlimited' && spouseCount >= spouseLimit) {
            paidCount++;
          }
          spouseCount++;
        } else if (beneficiary.relationship === 'child' && age >= childAgeThreshold) {
          paidCount++;
        } else if (['parent', 'grandparent', 'sibling', 'grandchild', 'other'].includes(beneficiary.relationship)) {
          paidCount++;
        }
      });

      return paidCount;
    },

    mobilePaymentMethods() {
      return this.paymentMethods.filter(method =>
          method.payment_type === 'MOBILE' && method.is_active
      );
    },

    bankPaymentMethods() {
      return this.paymentMethods.filter(method =>
          method.payment_type === 'BANK' && method.is_active
      );
    },

    cashPaymentMethod() {
      return this.paymentMethods.find(method =>
          method.payment_type === 'CASH' && method.is_active
      );
    },

    canProcessMobilePayment() {
      return !this.paymentLoading &&
          this.paymentForm.paymentMethodId &&
          this.paymentForm.mobileNumber &&
          this.mobileValidation.isValid &&
          this.paymentForm.mobileAmount > 0 &&
          this.paymentForm.mobileReference &&
          this.isPaymentReferenceValid('mobile');
    },

    canProcessCashPayment() {
      return !this.paymentLoading &&
          this.paymentForm.cashConfirmed &&
          this.paymentForm.cashAmount > 0;
    },

    canProcessBankPayment() {
      return !this.paymentLoading &&
          this.paymentForm.bankMethodId &&
          this.paymentForm.bankAmount > 0 &&
          this.paymentForm.bankReference &&
          this.isPaymentReferenceValid('bank');
    },

    availablePaymentMethods() {
      const methods = [];
      if (this.getRight('ACCEPT MOBILE MONEY PAYMENT')) methods.push('mobile');
      if (this.getRight('ACCEPT CASH PAYMENT')) methods.push('cash');
      if (this.getRight('ACCEPT BANK TRANSFER PAYMENT')) methods.push('bank');
      if (this.getRight('GENERATE AGENCY BANKING SLIP')) methods.push('agency_banking');
      return methods;
    },

    hasAnyPaymentMethod() {
      return this.availablePaymentMethods.length > 0;
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('paymentModal'));
    }
    this.loadPaymentMethods();
  },

  methods: {
    getRight(type) {
      return getRight(type);
    },

    async loadPaymentMethods() {
      const data = {
        "function": "getPaymentMethods"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.paymentMethods = res.data.data.paymentMethods || [];
          this.loadSavedPaymentPreferences();
        }
      } catch (e) {
        console.error("Failed to load payment methods:", e);
      }
    },

    getFreeImmediateFamilyList() {
      if (!this.beneficiariesData || !this.selectedPlan) {
        return [];
      }

      const childAgeThreshold = this.getChildAgeThreshold();
      const spouseLimit = this.getSpouseLimit();
      const freeList = [];
      let spouseCount = 0;

      this.beneficiariesData.forEach(beneficiary => {
        const fullName = beneficiary.full_name || beneficiary.fullName || 'N/A';
        const dateOfBirth = beneficiary.dateOfBirth || beneficiary.date_of_birth;
        const relationship = beneficiary.relationship || '';

        const age = this.calculateAge(dateOfBirth);

        if (relationship === 'spouse') {
          if (spouseLimit === 'unlimited' || spouseCount < spouseLimit) {
            freeList.push(`${fullName} (Spouse, ${age} years)`);
            spouseCount++;
          }
        } else if (relationship === 'child' && age < childAgeThreshold) {
          freeList.push(`${fullName} (Child, ${age} years)`);
        }
      });

      return freeList;
    },

    getPaidDependentsList() {
      if (!this.beneficiariesData || !this.selectedPlan) {
        return [];
      }

      const childAgeThreshold = this.getChildAgeThreshold();
      const spouseLimit = this.getSpouseLimit();
      const paidList = [];
      let spouseCount = 0;

      this.beneficiariesData.forEach(beneficiary => {
        const fullName = beneficiary.full_name || beneficiary.fullName || 'N/A';
        const dateOfBirth = beneficiary.dateOfBirth || beneficiary.date_of_birth;
        const relationship = beneficiary.relationship || '';

        const age = this.calculateAge(dateOfBirth);

        if (relationship === 'spouse') {
          if (spouseLimit !== 'unlimited' && spouseCount >= spouseLimit) {
            paidList.push(`${fullName} (Additional Spouse, ${age} years)`);
          }
          spouseCount++;
        } else if (relationship === 'child' && age >= childAgeThreshold) {
          paidList.push(`${fullName} (Adult Child, ${age} years)`);
        } else if (['parent', 'grandparent', 'sibling', 'grandchild', 'other'].includes(relationship)) {
          paidList.push(`${fullName} (${this.capitalizeFirst(relationship)}, ${age} years)`);
        }
      });

      return paidList;
    },

    getChildAgeThreshold() {
      if (!this.selectedPlan || !this.selectedPlan.beneficiary_rules) {
        return 18;
      }
      return this.selectedPlan.beneficiary_rules.free_immediate_family?.children_under_age || 18;
    },

    getSpouseLimit() {
      if (!this.selectedPlan || !this.selectedPlan.beneficiary_rules) {
        return 1;
      }
      const spouses = this.selectedPlan.beneficiary_rules.free_immediate_family?.included_spouses;
      if (spouses === 'unlimited' || spouses === 999) {
        return 'unlimited';
      }
      return spouses || 1;
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age;
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    validateMobileNumber() {
      let mobileNumber = this.paymentForm.mobileNumber.trim();

      this.mobileValidation.checking = false;
      this.mobileValidation.status = '';
      this.mobileValidation.isValid = false;

      if (!mobileNumber) {
        this.mobileValidation.message = 'Mobile number is required';
        this.mobileValidation.messageClass = 'text-danger';
        this.mobileValidation.icon = 'bx bx-error-circle';
        this.mobileValidation.status = 'invalid';
        return;
      }

      const cleanNumber = mobileNumber.replace(/\D/g, '');
      const ethiopianMobilePattern = /^[79]\d{8}$/;

      if (!ethiopianMobilePattern.test(cleanNumber)) {
        this.mobileValidation.status = 'invalid';
        this.mobileValidation.message = 'Invalid Ethiopian mobile number. Must be 9 digits starting with 7 or 9';
        this.mobileValidation.messageClass = 'text-danger';
        this.mobileValidation.icon = 'bx bx-error-circle';
        this.mobileValidation.isValid = false;
        return;
      }

      this.paymentForm.mobileNumber = cleanNumber;
      this.mobileValidation.status = 'valid';
      this.mobileValidation.message = 'Valid Ethiopian mobile number (+251' + cleanNumber + ')';
      this.mobileValidation.messageClass = 'text-success';
      this.mobileValidation.icon = 'bx bx-check-circle';
      this.mobileValidation.isValid = true;
    },

    getMobileNumberClass() {
      const baseClass = 'form-control';
      if (this.mobileValidation.status === 'valid') {
        return `${baseClass} is-valid`;
      } else if (this.mobileValidation.status === 'invalid') {
        return `${baseClass} is-invalid`;
      }
      return baseClass;
    },

    loadSavedPaymentPreferences() {
      try {
        const savedMobileMethod = localStorage.getItem('cbhi_preferred_mobile_method');
        if (savedMobileMethod && this.mobilePaymentMethods.length > 0) {
          const savedMethod = this.mobilePaymentMethods.find(m => m.method_id == savedMobileMethod);
          if (savedMethod) {
            this.paymentForm.paymentMethodId = savedMethod.method_id;
          }
        }

        const savedBankMethod = localStorage.getItem('cbhi_preferred_bank_method');
        if (savedBankMethod && this.bankPaymentMethods.length > 0) {
          const savedBank = this.bankPaymentMethods.find(b => b.method_id == savedBankMethod);
          if (savedBank) {
            this.paymentForm.bankMethodId = savedBank.method_id;
          }
        }

        const savedMobileNumber = localStorage.getItem('cbhi_mobile_number');
        if (savedMobileNumber) {
          const cleanNumber = savedMobileNumber.replace(/^\+251/, '');
          if (/^[79]\d{8}$/.test(cleanNumber)) {
            this.paymentForm.mobileNumber = cleanNumber;
            this.validateMobileNumber();
          }
        }
      } catch (e) {
        console.error("Error loading saved payment preferences:", e);
      }
    },

    savePaymentPreferences() {
      try {
        if (this.paymentForm.paymentMethodId) {
          localStorage.setItem('cbhi_preferred_mobile_method', this.paymentForm.paymentMethodId);
        }

        if (this.paymentForm.bankMethodId) {
          localStorage.setItem('cbhi_preferred_bank_method', this.paymentForm.bankMethodId);
        }

        if (this.paymentForm.mobileNumber && this.mobileValidation.isValid) {
          localStorage.setItem('cbhi_mobile_number', '+251' + this.paymentForm.mobileNumber);
        }
      } catch (e) {
        console.error("Error saving payment preferences:", e);
      }
    },

    openModal(paymentData = {}) {
      console.log('🔵 PaymentModal openModal called with:', paymentData);

      this.resetForm();

      this.balanceDue = paymentData.balanceDue || this.annualPremium;
      this.totalPaidPremiums = paymentData.totalPaidPremiums || 0;
      this.selectedPlan = paymentData.selectedPlan || null;
      this.fullAnnualPremium = paymentData.annualPremium || this.annualPremium;
      this.beneficiariesData = paymentData.beneficiaries || [];

      this.paymentForm.cashAmount = this.balanceDue;
      this.paymentForm.mobileAmount = this.balanceDue;
      this.paymentForm.bankAmount = this.balanceDue;

      this.loadSavedPaymentPreferences();

      if (!this.paymentForm.paymentMethodId && this.mobilePaymentMethods.length > 0) {
        this.paymentForm.paymentMethodId = this.mobilePaymentMethods[0].method_id;
      }

      if (!this.paymentForm.bankMethodId && this.bankPaymentMethods.length > 0) {
        this.paymentForm.bankMethodId = this.bankPaymentMethods[0].method_id;
      }

      if (this.availablePaymentMethods.length > 0) {
        this.selectedPaymentMethod = this.availablePaymentMethods[0];
      } else {
        console.warn('⚠️ No payment methods available for this user');
      }

      if (this.modalInstance) {
        this.modalInstance.show();
      }
    },

    resetForm() {
      this.paymentForm = {
        paymentMethodId: null,
        mobileNumber: '',
        mobileAmount: 0,
        cashAmount: 0,
        bankAmount: 0,
        cashConfirmed: false,
        mobileReference: '',
        bankReference: '',
        bankMethodId: null,
        notes: '',
        agencyBankingNotes: ''
      };

      this.mobileValidation = {
        checking: false,
        status: '',
        message: '',
        messageClass: 'text-muted',
        icon: 'bx bx-info-circle',
        isValid: false
      };

      this.paymentReferenceValidation = {
        checking: false,
        status: '',
        message: '',
        messageClass: 'text-muted',
        icon: 'bx bx-info-circle',
        isValid: false,
        fieldType: ''
      };

      this.processing = false;
      this.paymentLoading = false;
      this.transactionSuccess = false;
      this.selectedPaymentMethod = 'mobile';
      this.paymentResult = {};
      this.balanceDue = 0;
      this.totalPaidPremiums = 0;
      this.selectedPlan = null;
      this.fullAnnualPremium = 0;
      this.beneficiariesData = [];
      this.showAgencyBankingSlip = false;
    },

    validatePaymentReference(fieldType) {
      let reference = '';
      if (fieldType === 'mobile') {
        reference = this.paymentForm.mobileReference.trim();
      } else if (fieldType === 'bank') {
        reference = this.paymentForm.bankReference.trim();
      }

      this.paymentReferenceValidation.checking = false;
      this.paymentReferenceValidation.status = '';
      this.paymentReferenceValidation.isValid = false;
      this.paymentReferenceValidation.fieldType = fieldType;

      if (!reference) {
        this.paymentReferenceValidation.message = 'Payment reference is required';
        this.paymentReferenceValidation.messageClass = 'text-danger';
        this.paymentReferenceValidation.icon = 'bx bx-error-circle';
        this.paymentReferenceValidation.status = 'invalid';
        return;
      }

      if (reference.length < 3) {
        this.paymentReferenceValidation.message = 'Payment reference must be at least 3 characters';
        this.paymentReferenceValidation.messageClass = 'text-danger';
        this.paymentReferenceValidation.icon = 'bx bx-error-circle';
        this.paymentReferenceValidation.status = 'invalid';
        return;
      }

      this.paymentReferenceValidation.message = 'Checking availability...';
      this.paymentReferenceValidation.messageClass = 'text-info';
      this.paymentReferenceValidation.icon = 'bx bx-info-circle';
    },

    async checkPaymentReferenceAvailability(fieldType) {
      let reference = '';
      if (fieldType === 'mobile') {
        reference = this.paymentForm.mobileReference.trim();
      } else if (fieldType === 'bank') {
        reference = this.paymentForm.bankReference.trim();
      }

      if (!reference || reference.length < 3) {
        return;
      }

      this.paymentReferenceValidation.checking = true;
      this.paymentReferenceValidation.fieldType = fieldType;

      const data = {
        "function": "checkPaymentReferenceAvailability",
        "paymentReference": reference
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          if (res.data.data.isAvailable) {
            this.paymentReferenceValidation.status = 'valid';
            this.paymentReferenceValidation.message = 'Payment reference is available';
            this.paymentReferenceValidation.messageClass = 'text-success';
            this.paymentReferenceValidation.icon = 'bx bx-check-circle';
            this.paymentReferenceValidation.isValid = true;
          } else {
            this.paymentReferenceValidation.status = 'invalid';
            this.paymentReferenceValidation.message = `Reference already used: ${res.data.data.existingPayment}`;
            this.paymentReferenceValidation.messageClass = 'text-danger';
            this.paymentReferenceValidation.icon = 'bx bx-x-circle';
            this.paymentReferenceValidation.isValid = false;
          }
        } else {
          this.paymentReferenceValidation.status = 'invalid';
          this.paymentReferenceValidation.message = 'Error checking payment reference availability';
          this.paymentReferenceValidation.messageClass = 'text-danger';
          this.paymentReferenceValidation.icon = 'bx bx-error-circle';
          this.paymentReferenceValidation.isValid = false;
        }
      } catch (e) {
        console.error("Failed to check payment reference:", e);
        this.paymentReferenceValidation.status = 'invalid';
        this.paymentReferenceValidation.message = 'Error checking payment reference availability';
        this.paymentReferenceValidation.messageClass = 'text-danger';
        this.paymentReferenceValidation.icon = 'bx bx-error-circle';
        this.paymentReferenceValidation.isValid = false;
      } finally {
        this.paymentReferenceValidation.checking = false;
      }
    },

    getPaymentReferenceClass(fieldType) {
      const baseClass = 'form-control';
      if (this.paymentReferenceValidation.fieldType === fieldType) {
        if (this.paymentReferenceValidation.status === 'valid') {
          return `${baseClass} is-valid`;
        } else if (this.paymentReferenceValidation.status === 'invalid') {
          return `${baseClass} is-invalid`;
        }
      }
      return baseClass;
    },

    isPaymentReferenceValid(fieldType) {
      return this.paymentReferenceValidation.fieldType === fieldType &&
          this.paymentReferenceValidation.isValid;
    },

    async processPayment() {
      this.paymentLoading = true;
      this.processing = true;

      let paymentAmount = this.balanceDue;
      let paymentReference = '';
      let paymentMethodId = null;
      let mobileNumber = '';

      if (this.selectedPaymentMethod === 'cash') {
        paymentAmount = parseFloat(this.paymentForm.cashAmount);
        paymentMethodId = this.cashPaymentMethod?.method_id;
        paymentReference = '';
      } else if (this.selectedPaymentMethod === 'mobile') {
        paymentAmount = parseFloat(this.paymentForm.mobileAmount);
        paymentMethodId = this.paymentForm.paymentMethodId;
        paymentReference = this.paymentForm.mobileReference;
        mobileNumber = '+251' + this.paymentForm.mobileNumber;
      } else if (this.selectedPaymentMethod === 'bank') {
        paymentAmount = parseFloat(this.paymentForm.bankAmount);
        paymentMethodId = this.paymentForm.bankMethodId;
        paymentReference = this.paymentForm.bankReference;
      }

      if (!paymentMethodId) {
        this.processing = false;
        this.paymentLoading = false;
        this.$emit('payment-error', {
          message: 'Please select a valid payment method'
        });
        return;
      }

      const paymentData = {
        "function": "recordPremiumPayment",
        "cbhiNumber": this.memberInfo.cbhi_number || this.memberInfo.cbhiNumber,
        "paymentMethodId": paymentMethodId,
        "amount": paymentAmount,
        "paymentYear": new Date().getFullYear(),
        "receiptNumber": this.generateReceiptNumber(),
        "paymentReference": paymentReference,
        "mobileNumber": mobileNumber,
        "notes": `Premium payment for ${this.memberFullName}. Balance payment of ETB ${this.formatCurrency(this.balanceDue)} (Total Premium: ETB ${this.formatCurrency(this.fullAnnualPremium)}). ${this.paymentForm.notes || ''}`.trim(),
        "collectionPoint": sessionStorage.getItem("hospitalName") || "Main Office",
        "collectedBy": sessionStorage.getItem("userName") || "System"
      };

      try {
        const res = await executeJson(paymentData, authUrl);

        if (res.data.success) {
          this.savePaymentPreferences();

          this.processing = false;
          this.transactionSuccess = true;
          this.paymentResult = res.data.data;

          this.paymentResult = {
            payment_id: res.data.data.payment_id,
            payment_reference: res.data.data.paymentReference,
            receipt_number: res.data.data.receiptNumber,
            payment_method: res.data.data.paymentMethod,
            receiptNumber: res.data.data.receiptNumber || this.generateReceiptNumber(),
            ...res.data.data
          };

          this.$emit('payment-recorded', {
            payment_id: res.data.data.payment_id,
            payment: res.data.data,
            message: 'Premium payment recorded successfully'
          });
        } else {
          this.processing = false;
          this.$emit('payment-error', {
            message: res.data.message || 'Failed to record payment'
          });
        }
      } catch (e) {
        this.processing = false;
        this.$emit('payment-error', {
          message: e.message || 'Payment processing failed'
        });
      } finally {
        this.paymentLoading = false;
      }
    },

    generateReceiptNumber() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      return `RCP-${year}${month}${day}-${random}`;
    },

    generateAgencyBankingSlip() {
      this.showAgencyBankingSlip = true;
    },

    closeAgencyBankingSlip() {
      this.showAgencyBankingSlip = false;
    },

    printPaymentSlip() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');

      const printStyles = `
        <style>
          @page {
            size: A4 portrait;
            margin: 8mm;
          }

          @media print {
            body {
              margin: 0;
              padding: 8px;
              font-size: 10px;
              line-height: 1.2;
            }
            .no-print { display: none !important; }
            .card {
              page-break-inside: avoid;
              margin-bottom: 4px !important;
              border: 1px solid #000;
            }
            .card-header {
              padding: 4px 6px !important;
              font-size: 10px !important;
            }
            .card-body {
              padding: 5px !important;
            }
            .alert {
              border: 1px solid #000;
              padding: 4px !important;
              margin-bottom: 4px !important;
              font-size: 9px !important;
            }
            table {
              page-break-inside: avoid;
              font-size: 9px !important;
            }
            .table th, .table td {
              padding: 2px !important;
            }
            h3 { font-size: 13px !important; margin: 2px 0 !important; }
            h4 { font-size: 11px !important; margin: 2px 0 !important; }
            h5 { font-size: 10px !important; margin: 2px 0 !important; }
            h6 { font-size: 9px !important; margin: 2px 0 !important; }
            .small, small { font-size: 8px !important; }
            .mb-1, .mb-2, .mb-3, .mb-4 { margin-bottom: 3px !important; }
            .row { margin-bottom: 3px !important; }
            ol li { margin-bottom: 2px !important; padding-left: 2px !important; line-height: 1.2 !important; }
            img { max-height: 35px !important; }
            svg { width: 120px !important; height: 35px !important; }
            .slip-header { padding-bottom: 5px !important; margin-bottom: 5px !important; }
          }

          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .payment-slip-container { padding: 8px; }
          .card { border: 1px solid #dee2e6; margin-bottom: 0.5rem; }
          .card-header { background-color: #f8f9fa; padding: 0.4rem 0.6rem; border-bottom: 1px solid #dee2e6; font-weight: 600; }
          .card-body { padding: 0.6rem; }
          .table { width: 100%; border-collapse: collapse; }
          .table th, .table td { padding: 0.3rem; border: 1px solid #dee2e6; }
          .table-bordered { border: 1px solid #dee2e6; }
          .alert { padding: 0.5rem; margin-bottom: 0.5rem; border-radius: 0.25rem; }
          .alert-warning { background-color: #fff3cd; border-color: #ffc107; }
          .alert-success { background-color: #d1e7dd; border-color: #28a745; }
          .alert-info { background-color: #cfe2ff; border-color: #0dcaf0; }
          .text-primary { color: #0d6efd !important; }
          .text-success { color: #28a745 !important; }
          .text-danger { color: #dc3545 !important; }
          .text-warning { color: #ffc107 !important; }
          .text-muted { color: #6c757d !important; }
          .bg-primary { background-color: #0d6efd !important; color: white !important; }
          .bg-success { background-color: #28a745 !important; color: white !important; }
          .bg-warning { background-color: #ffc107 !important; }
          .bg-info { background-color: #0dcaf0 !important; color: white !important; }
          .bg-secondary { background-color: #6c757d !important; color: white !important; }
          .fw-bold { font-weight: bold; }
          .text-center { text-align: center; }
          .text-end { text-align: right; }
          .mb-0 { margin-bottom: 0; }
          .mb-1 { margin-bottom: 0.25rem; }
          .mb-2 { margin-bottom: 0.4rem; }
          .mb-3 { margin-bottom: 0.6rem; }
          .mb-4 { margin-bottom: 0.8rem; }
          .mt-2 { margin-top: 0.4rem; }
          .mt-3 { margin-top: 0.6rem; }
          .row { display: flex; flex-wrap: wrap; margin: 0 -0.4rem; }
          .col-12 { flex: 0 0 100%; max-width: 100%; padding: 0 0.4rem; }
          .col-md-4 { flex: 0 0 33.333%; max-width: 33.333%; padding: 0 0.4rem; }
          .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 0.4rem; }
          .border { border: 1px solid #dee2e6 !important; }
          .border-dashed { border-style: dashed !important; }
          .border-bottom { border-bottom: 2px solid #dee2e6 !important; }
          .font-monospace { font-family: 'Courier New', monospace; }
          .d-flex { display: flex; }
          .justify-content-between { justify-content: space-between; }
          .align-items-center { align-items: center; }
          .text-uppercase { text-transform: uppercase; }
        </style>
      `;

      const printContent = document.getElementById('paymentSlipPrintArea').innerHTML;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>CBHI Payment Slip</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          ${printContent}
          <script type="text/javascript">
            window.onload = function() {
              window.print();
            };
          <\/script>
        </body>
        </html>
      `);

      printWindow.document.close();
    },

    getFullAddress() {
      const parts = [
        this.memberInfo.address,
        this.memberInfo.kebele_name,
        this.memberInfo.woreda_name,
        this.memberInfo.zone_name,
        this.memberInfo.region_name
      ].filter(Boolean);

      return parts.join(', ') || 'N/A';
    },

    getCurrentDateTime() {
      const now = new Date();
      return now.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getCurrentYear() {
      return new Date().getFullYear();
    },

    getUsername() {
      return sessionStorage.getItem('userName') || localStorage.getItem('username') || 'CBHI Officer';
    },

    generateReferenceNumber() {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      const cbhiNum = this.memberInfo.cbhi_number || this.memberInfo.cbhiNumber || 'UNKNOWN';
      return `PAY-${cbhiNum}-${timestamp}-${random}`;
    },

    viewReceipt() {
      this.$emit('download-receipt', {
        payment: this.paymentResult,
        memberInfo: this.memberInfo,
        totalBeneficiaries: this.totalBeneficiaries,
        annualPremium: this.fullAnnualPremium,
        balanceDue: this.balanceDue,
        totalPaidPremiums: this.totalPaidPremiums
      });

      setTimeout(() => {
        this.closeModal();
      }, 500);
    },

    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
      this.resetForm();
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }

  .payment-slip-container {
    width: 100%;
    padding: 10px !important;
    font-size: 11px !important;
  }

  .card {
    page-break-inside: avoid;
    border: 1px solid #000 !important;
    margin-bottom: 8px !important;
  }

  .card-header {
    padding: 6px 10px !important;
    font-size: 12px !important;
  }

  .card-body {
    padding: 8px !important;
  }

  .slip-header {
    border-bottom: 2px solid #000 !important;
    padding-bottom: 8px !important;
    margin-bottom: 8px !important;
  }

  .slip-header h3 {
    font-size: 16px !important;
    margin-bottom: 4px !important;
  }

  .slip-header p {
    font-size: 10px !important;
  }

  table {
    page-break-inside: avoid;
    font-size: 10px !important;
  }

  .table th, .table td {
    padding: 4px !important;
  }

  .alert {
    padding: 8px !important;
    margin-bottom: 8px !important;
    font-size: 10px !important;
  }

  .mb-2 {
    margin-bottom: 8px !important;
  }

  .mb-3 {
    margin-bottom: 8px !important;
  }

  h4, h5, h6 {
    font-size: 12px !important;
    margin-bottom: 4px !important;
  }

  .small, small {
    font-size: 9px !important;
  }

  ol li {
    margin-bottom: 4px !important;
    line-height: 1.3 !important;
  }

  .row {
    margin-bottom: 6px !important;
  }
}

.modal.show {
  display: block !important;
}

.modal-header.bg-primary {
  background-color: #007bff !important;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-control.is-valid {
  border-color: #28a745;
  padding-right: calc(1.5em + 0.75rem);
  background-image: none;
}

.form-control.is-invalid {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem);
  background-image: none;
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}

.top-50 {
  top: 50%;
}

.end-0 {
  right: 0;
}

.translate-middle-y {
  transform: translateY(-50%);
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.text-danger {
  color: #dc3545 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-muted {
  color: #6c757d !important;
}

.text-success {
  color: #28a745 !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.input-group-text {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  color: #495057;
  font-weight: 500;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  margin: 0 auto;
}

.check-icon {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 50%;
  box-sizing: content-box;
  border: 4px solid #4CAF50;
}

.check-icon::before {
  top: 3px;
  left: -2px;
  width: 30px;
  transform-origin: 100% 50%;
  border-radius: 100px 0 0 100px;
}

.check-icon::after {
  top: 0;
  left: 30px;
  width: 60px;
  transform-origin: 0 50%;
  border-radius: 0 100px 100px 0;
  animation: rotate-circle 4.25s ease-in;
}

.icon-line {
  height: 5px;
  background-color: #4CAF50;
  display: block;
  border-radius: 2px;
  position: absolute;
  z-index: 10;
}

.icon-line.line-tip {
  top: 46px;
  left: 14px;
  width: 25px;
  transform: rotate(45deg);
  animation: icon-line-tip 0.75s;
}

.icon-line.line-long {
  top: 38px;
  right: 8px;
  width: 47px;
  transform: rotate(-45deg);
  animation: icon-line-long 0.75s;
}

.icon-circle {
  top: -4px;
  left: -4px;
  z-index: 10;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  box-sizing: content-box;
  border: 4px solid rgba(76, 175, 80, .5);
}

.icon-fix {
  top: 8px;
  width: 5px;
  left: 26px;
  z-index: 1;
  height: 85px;
  position: absolute;
  transform: rotate(-45deg);
  background-color: #FFFFFF;
}

@keyframes rotate-circle {
  0% {
    transform: rotate(-45deg);
  }
  5% {
    transform: rotate(-45deg);
  }
  12% {
    transform: rotate(-405deg);
  }
  100% {
    transform: rotate(-405deg);
  }
}

@keyframes icon-line-tip {
  0% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  54% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  70% {
    width: 50px;
    left: -8px;
    top: 37px;
  }
  84% {
    width: 17px;
    left: 21px;
    top: 48px;
  }
  100% {
    width: 25px;
    left: 14px;
    top: 45px;
  }
}

@keyframes icon-line-long {
  0% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  65% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  84% {
    width: 55px;
    right: 0px;
    top: 35px;
  }
  100% {
    width: 47px;
    right: 8px;
    top: 38px;
  }
}

.agency-banking-slip {
  max-height: 80vh;
  overflow-y: auto;
}

.payment-slip-container {
  background: white;
  max-width: 900px;
  margin: 0 auto;
}

.slip-header {
  border-bottom: 3px solid #dee2e6;
}

.card {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border-radius: 0.375rem;
}

.card-header {
  font-weight: 600;
  border-bottom: 1px solid rgba(0,0,0,0.125);
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
}
</style>